import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Account, Tenant, Voucher } from 'src/models/account.model';
import { AuthService } from 'src/services/auth.service';
import { TenantService } from 'src/services/tenant.service';
import { VoucherService } from 'src/services/voucher.service';
import { AccountService } from 'src/services/account.service';
import Swal from 'sweetalert2';

declare var html2pdf: any;

interface TenantSession {
  tenant_id: string;
  account_key: string;
  store_name: string;
  tenant_name: string;
  username: string;
  logged_in_at: Date;
  expires_at: Date;
}

@Component({
  selector: 'app-voucher-generator',
  templateUrl: './voucher-generator.component.html',
  styleUrls: ['./voucher-generator.component.css']
})
export class VoucherGeneratorComponent implements OnInit {

  tenantSession: TenantSession | null = null;
  account: Account | null = null;
  tenant: Tenant | null = null;
  isLoading: boolean = true;

  generationMode: 'single' | 'batch' = 'single';
  batchQuantity: number = 5;
  maxBatchQuantity: number = 50;

  generatedVouchers: Voucher[] = [];
  isGenerating: boolean = false;

  // Actual voucher count from Firebase
  actualVoucherCount: number = 0;

  constructor(
    private router: Router,
    private authService: AuthService,
    private tenantService: TenantService,
    private voucherService: VoucherService,
    private accountService: AccountService
  ) { }

  async ngOnInit() {
    await this.loadData();
  }

  async loadData() {
    this.isLoading = true;

    const sessionStr = localStorage.getItem('tenant_session');
    if (!sessionStr) {
      this.router.navigate(['/']);
      return;
    }

    this.tenantSession = JSON.parse(sessionStr);

    try {
      this.account = await this.authService.getAccountByKey(this.tenantSession.account_key);

      if (this.account) {
        this.tenant = this.tenantService.getTenantById(this.account, this.tenantSession.tenant_id);

        // Get actual voucher count from Firebase (with period filtering)
        const resetPeriod = this.tenant?.voucher_settings?.reset_period;
        this.actualVoucherCount = await this.voucherService.getVoucherCountForPeriod(this.tenantSession.tenant_id, resetPeriod);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }

    this.isLoading = false;
  }

  canGenerateVouchers(quantity: number = 1): { allowed: boolean; reason?: string } {
    if (!this.tenant) return { allowed: false, reason: 'Tenant not found' };

    // Use actual count from Firebase for limit checking
    const settings = this.tenant.voucher_settings;
    if (!settings) return { allowed: true };

    if (settings.generation_limit !== null && settings.generation_limit !== undefined) {
      const remaining = settings.generation_limit - this.actualVoucherCount;
      if (remaining <= 0) {
        return {
          allowed: false,
          reason: 'Voucher generation limit reached. You cannot generate more vouchers.'
        };
      }
      if (remaining < quantity) {
        return {
          allowed: false,
          reason: `You can only generate ${remaining} more voucher(s).`
        };
      }
    }
    return { allowed: true };
  }

  getRemainingVouchers(): number {
    const settings = this.tenant?.voucher_settings;
    if (!settings || settings.generation_limit === null || settings.generation_limit === undefined) {
      return Infinity;
    }
    // Use actual count from Firebase instead of cached vouchers_generated
    return Math.max(0, settings.generation_limit - this.actualVoucherCount);
  }

  getUsagePercentage(): number {
    const settings = this.tenant?.voucher_settings;
    if (!settings || settings.generation_limit === null || settings.generation_limit === undefined || settings.generation_limit === 0) {
      return 0;
    }
    // Use actual count from Firebase instead of cached vouchers_generated
    return Math.min(100, (this.actualVoucherCount / settings.generation_limit) * 100);
  }

  async generateSingle() {
    const check = this.canGenerateVouchers(1);
    if (!check.allowed) {
      Swal.fire({
        icon: 'warning',
        title: 'Cannot Generate',
        text: check.reason
      });
      return;
    }

    this.isGenerating = true;
    this.generatedVouchers = [];

    try {
      const voucher = await this.voucherService.generateVoucher(this.account, this.tenant);
      this.generatedVouchers = [voucher];

      // Refresh account and tenant data
      await this.refreshData();

      Swal.fire({
        icon: 'success',
        title: 'Voucher Generated!',
        text: `Code: ${voucher.voucher_code}`,
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error generating voucher:', error);

      let errorMessage = 'Failed to generate voucher. Please try again.';

      if (error?.message?.includes('authenticated')) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (error?.message?.includes('permission')) {
        errorMessage = 'Permission denied. Please check your account permissions.';
      } else if (error?.message) {
        errorMessage = error.message;
      }

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage
      });
    }

    this.isGenerating = false;
  }

  async generateBatch() {
    if (this.batchQuantity < 1 || this.batchQuantity > this.maxBatchQuantity) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Quantity',
        text: `Please enter a quantity between 1 and ${this.maxBatchQuantity}.`
      });
      return;
    }

    const check = this.canGenerateVouchers(this.batchQuantity);
    if (!check.allowed) {
      Swal.fire({
        icon: 'warning',
        title: 'Cannot Generate',
        text: check.reason
      });
      return;
    }

    this.isGenerating = true;
    this.generatedVouchers = [];

    try {
      const vouchers = await this.voucherService.generateBatchVouchers(
        this.account,
        this.tenant,
        this.batchQuantity
      );
      this.generatedVouchers = vouchers;

      // Refresh account and tenant data
      await this.refreshData();

      Swal.fire({
        icon: 'success',
        title: 'Vouchers Generated!',
        text: `${vouchers.length} vouchers have been created.`,
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error generating vouchers:', error);

      let errorMessage = 'Failed to generate vouchers. Please try again.';

      if (error?.message?.includes('authenticated')) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (error?.message?.includes('permission')) {
        errorMessage = 'Permission denied. Please check your account permissions.';
      } else if (error?.message) {
        errorMessage = error.message;
      }

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage
      });
    }

    this.isGenerating = false;
  }

  async refreshData() {
    try {
      this.account = await this.authService.getAccountByKey(this.tenantSession.account_key);
      if (this.account) {
        this.tenant = this.tenantService.getTenantById(this.account, this.tenantSession.tenant_id);
      }
      // Refresh actual voucher count from Firebase (with period filtering)
      const resetPeriod = this.tenant?.voucher_settings?.reset_period;
      this.actualVoucherCount = await this.voucherService.getVoucherCountForPeriod(this.tenantSession.tenant_id, resetPeriod);
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  }

  formatDate(date: Date | any): string {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date.seconds * 1000);
    return d.toLocaleString();
  }

  async printVouchers() {
    const element = document.getElementById('voucher-print-area');
    if (!element) return;

    const options = {
      margin: 10,
      filename: `vouchers-${this.tenant.store_name}-${new Date().getTime()}.pdf`,
      image: { type: 'png', quality: 1 },
      html2canvas: { scale: 2, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    await Swal.fire({
      title: 'Generating PDF...',
      allowEscapeKey: false,
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
        html2pdf().set(options).from(element).save().then(() => {
          Swal.close();
        });
      }
    });
  }

  clearVouchers() {
    this.generatedVouchers = [];
  }

  getResetPeriodLabel(): string {
    return this.tenant?.voucher_settings?.reset_period === 'daily' ? 'Resets daily' : '';
  }

  hasResetPeriod(): boolean {
    return this.tenant?.voucher_settings?.reset_period === 'daily';
  }
}
